import { useEffect, useMemo, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import type {
  MembershipStatus,
  PlanTier,
  PreviewUserOutreachEmailResponse,
  UserOutreachAudiencePageResponse,
  UserOutreachCampaignResponse,
  UserOutreachFilters,
  UserRole,
} from '@tilly/shared';
import { adminOutreachApi } from '@/api/client';

const triStateOptions = [
  { value: 'any', label: 'Any' },
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
] as const;

function dateToStart(value: string): number | undefined {
  return value ? new Date(`${value}T00:00:00`).getTime() : undefined;
}

function dateToEnd(value: string): number | undefined {
  return value ? new Date(`${value}T23:59:59.999`).getTime() : undefined;
}

function formatDate(value?: number): string {
  return value ? new Date(value).toLocaleDateString() : '—';
}

function RangeFields({
  label,
  value,
  onChange,
}: {
  label: string;
  value?: { min?: number; max?: number };
  onChange: (value: { min?: number; max?: number } | undefined) => void;
}) {
  const update = (key: 'min' | 'max', raw: string) => {
    const next = { ...value, [key]: raw === '' ? undefined : Math.max(0, Number(raw)) };
    onChange(next.min === undefined && next.max === undefined ? undefined : next);
  };
  return (
    <Box>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Box sx={{ display: 'flex', gap: 1 }}>
        <TextField
          size="small"
          type="number"
          label="Min"
          value={value?.min ?? ''}
          onChange={(event) => update('min', event.target.value)}
          inputProps={{ min: 0 }}
        />
        <TextField
          size="small"
          type="number"
          label="Max"
          value={value?.max ?? ''}
          onChange={(event) => update('max', event.target.value)}
          inputProps={{ min: 0 }}
        />
      </Box>
    </Box>
  );
}

export function UserOutreachPage() {
  const { user } = useAuth0();
  const [filters, setFilters] = useState<UserOutreachFilters>({
    membershipPresence: 'any',
    emailEligibility: 'any',
    addressComplete: 'any',
    paymentConnected: 'any',
    remindersEnabled: 'any',
    setupReady: 'any',
  });
  const [createdFrom, setCreatedFrom] = useState('');
  const [createdTo, setCreatedTo] = useState('');
  const [lastBookingFrom, setLastBookingFrom] = useState('');
  const [lastBookingTo, setLastBookingTo] = useState('');
  const [audience, setAudience] = useState<UserOutreachAudiencePageResponse | null>(null);
  const [filtersDirty, setFiltersDirty] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [subject, setSubject] = useState('');
  const [markdownBody, setMarkdownBody] = useState('');
  const [review, setReview] = useState<PreviewUserOutreachEmailResponse | null>(null);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [testSending, setTestSending] = useState(false);
  const [testSent, setTestSent] = useState(false);
  const [confirmCount, setConfirmCount] = useState('');
  const [campaign, setCampaign] = useState<UserOutreachCampaignResponse | null>(null);

  const updateFilter = <K extends keyof UserOutreachFilters>(
    key: K,
    value: UserOutreachFilters[K]
  ) => {
    setFilters((current) => ({ ...current, [key]: value }));
    if (audience) setFiltersDirty(true);
  };

  useEffect(() => {
    setReview(null);
    setTestSent(false);
    setConfirmCount('');
  }, [subject, markdownBody]);

  useEffect(() => {
    if (!campaign || (campaign.status !== 'queued' && campaign.status !== 'sending')) return;
    const interval = window.setInterval(async () => {
      try {
        setCampaign(await adminOutreachApi.getCampaign(campaign.id));
      } catch {
        // Preserve the latest known progress; the next poll can recover.
      }
    }, 1500);
    return () => window.clearInterval(interval);
  }, [campaign]);

  const appliedFilters = useMemo(
    () => ({
      ...filters,
      createdFrom: dateToStart(createdFrom),
      createdTo: dateToEnd(createdTo),
      lastBookingFrom: dateToStart(lastBookingFrom),
      lastBookingTo: dateToEnd(lastBookingTo),
    }),
    [filters, createdFrom, createdTo, lastBookingFrom, lastBookingTo]
  );

  const buildAudience = async () => {
    setLoading(true);
    setError(null);
    setCampaign(null);
    try {
      const result = await adminOutreachApi.createAudience({ filters: appliedFilters });
      setAudience(result);
      setFiltersDirty(false);
      setReview(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to build audience');
    } finally {
      setLoading(false);
    }
  };

  const loadPage = async (page: number) => {
    if (!audience) return;
    setLoading(true);
    setError(null);
    try {
      setAudience(await adminOutreachApi.getAudiencePage(audience.audienceId, page));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load page');
    } finally {
      setLoading(false);
    }
  };

  const openReview = async () => {
    if (!audience || filtersDirty) return;
    setLoading(true);
    setError(null);
    try {
      const result = await adminOutreachApi.previewEmail(audience.audienceId, {
        subject,
        markdownBody,
      });
      setReview(result);
      setReviewOpen(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to render email preview');
    } finally {
      setLoading(false);
    }
  };

  const sendTest = async () => {
    if (!audience || !review || !user?.email) return;
    setTestSending(true);
    setError(null);
    try {
      await adminOutreachApi.sendTestEmail(audience.audienceId, {
        subject,
        markdownBody,
        sampleUserId: review.sampleUserId,
        recipient: user.email,
      });
      setTestSent(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send test email');
    } finally {
      setTestSending(false);
    }
  };

  const startCampaign = async () => {
    if (!audience || !review || !user?.email) return;
    setLoading(true);
    setError(null);
    try {
      const result = await adminOutreachApi.createCampaign(audience.audienceId, {
        subject,
        markdownBody,
        contentHash: review.contentHash,
        confirmedRecipientCount: Number(confirmCount),
        initiatedBy: user.email,
      });
      setCampaign(result);
      setReviewOpen(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start campaign');
    } finally {
      setLoading(false);
    }
  };

  const renderTriState = (
    label: string,
    key: 'addressComplete' | 'paymentConnected' | 'remindersEnabled' | 'setupReady'
  ) => (
    <FormControl fullWidth size="small">
      <InputLabel>{label}</InputLabel>
      <Select
        label={label}
        value={filters[key] ?? 'any'}
        onChange={(event) =>
          updateFilter(key, event.target.value as UserOutreachFilters[typeof key])
        }
      >
        {triStateOptions.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  return (
    <Container maxWidth="xl">
      <Box sx={{ py: 4 }}>
        <Typography variant="h4" sx={{ mb: 1 }}>
          User Outreach
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 3 }}>
          Find registered users by account and business health, then review a targeted email before
          sending.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Audience filters
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                label="Search user or business"
                value={filters.search ?? ''}
                onChange={(event) => updateFilter('search', event.target.value || undefined)}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="Signed up after"
                InputLabelProps={{ shrink: true }}
                value={createdFrom}
                onChange={(event) => {
                  setCreatedFrom(event.target.value);
                  if (audience) setFiltersDirty(true);
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="Signed up before"
                InputLabelProps={{ shrink: true }}
                value={createdTo}
                onChange={(event) => {
                  setCreatedTo(event.target.value);
                  if (audience) setFiltersDirty(true);
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Membership</InputLabel>
                <Select
                  label="Membership"
                  value={filters.membershipPresence ?? 'any'}
                  onChange={(event) =>
                    updateFilter(
                      'membershipPresence',
                      event.target.value as UserOutreachFilters['membershipPresence']
                    )
                  }
                >
                  <MenuItem value="any">Any</MenuItem>
                  <MenuItem value="none">No membership</MenuItem>
                  <MenuItem value="has_membership">Has membership</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Email eligibility</InputLabel>
                <Select
                  label="Email eligibility"
                  value={filters.emailEligibility ?? 'any'}
                  onChange={(event) =>
                    updateFilter(
                      'emailEligibility',
                      event.target.value as UserOutreachFilters['emailEligibility']
                    )
                  }
                >
                  <MenuItem value="any">Any</MenuItem>
                  <MenuItem value="eligible">Eligible</MenuItem>
                  <MenuItem value="ineligible">Ineligible</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Preferred language</InputLabel>
                <Select
                  label="Preferred language"
                  value={filters.preferredLanguage ?? ''}
                  onChange={(event) =>
                    updateFilter('preferredLanguage', event.target.value || undefined)
                  }
                >
                  <MenuItem value="">Any</MenuItem>
                  <MenuItem value="en-US">English</MenuItem>
                  <MenuItem value="es-MX">Spanish (Mexico)</MenuItem>
                  <MenuItem value="uncaptured">Not captured</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={4} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Role</InputLabel>
                <Select
                  label="Role"
                  value={filters.membershipRoles?.[0] ?? ''}
                  onChange={(event) =>
                    updateFilter(
                      'membershipRoles',
                      event.target.value ? [event.target.value as UserRole] : undefined
                    )
                  }
                >
                  <MenuItem value="">Any</MenuItem>
                  {['owner', 'admin', 'provider', 'customer'].map((role) => (
                    <MenuItem key={role} value={role}>
                      {role}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  label="Status"
                  value={filters.membershipStatuses?.[0] ?? ''}
                  onChange={(event) =>
                    updateFilter(
                      'membershipStatuses',
                      event.target.value ? [event.target.value as MembershipStatus] : undefined
                    )
                  }
                >
                  <MenuItem value="">Any</MenuItem>
                  {['pending', 'approved', 'rejected', 'active', 'suspended'].map((status) => (
                    <MenuItem key={status} value={status}>
                      {status}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Subscription</InputLabel>
                <Select
                  label="Subscription"
                  value={filters.subscriptionTiers?.[0] ?? ''}
                  onChange={(event) =>
                    updateFilter(
                      'subscriptionTiers',
                      event.target.value ? [event.target.value as PlanTier] : undefined
                    )
                  }
                >
                  <MenuItem value="">Any</MenuItem>
                  <MenuItem value="free">Free</MenuItem>
                  <MenuItem value="solo">Solo</MenuItem>
                  <MenuItem value="team">Team</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <RangeFields
                label="Active primary services"
                value={filters.activeServiceCount}
                onChange={(value) => updateFilter('activeServiceCount', value)}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <RangeFields
                label="Open business days"
                value={filters.openDayCount}
                onChange={(value) => updateFilter('openDayCount', value)}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <RangeFields
                label="Active providers"
                value={filters.activeProviderCount}
                onChange={(value) => updateFilter('activeProviderCount', value)}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <RangeFields
                label="Scheduled providers"
                value={filters.scheduledProviderCount}
                onChange={(value) => updateFilter('scheduledProviderCount', value)}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <RangeFields
                label="Appointments"
                value={filters.appointmentCount}
                onChange={(value) => updateFilter('appointmentCount', value)}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="Last booking after"
                InputLabelProps={{ shrink: true }}
                value={lastBookingFrom}
                onChange={(event) => {
                  setLastBookingFrom(event.target.value);
                  if (audience) setFiltersDirty(true);
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="Last booking before"
                InputLabelProps={{ shrink: true }}
                value={lastBookingTo}
                onChange={(event) => {
                  setLastBookingTo(event.target.value);
                  if (audience) setFiltersDirty(true);
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              {renderTriState('Address complete', 'addressComplete')}
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              {renderTriState('Payments connected', 'paymentConnected')}
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              {renderTriState('Reminders enabled', 'remindersEnabled')}
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              {renderTriState('Core setup ready', 'setupReady')}
            </Grid>
          </Grid>
          <Box sx={{ mt: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
            <Button variant="contained" onClick={buildAudience} disabled={loading}>
              {loading ? <CircularProgress size={22} /> : 'Apply filters'}
            </Button>
            {filtersDirty && (
              <Typography color="warning.main" variant="body2">
                Filters changed. Apply them again before reviewing email.
              </Typography>
            )}
          </Box>
        </Paper>

        {audience && (
          <>
            <Paper sx={{ p: 2, mb: 2 }}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                <Chip label={`${audience.totalMatches} matches`} />
                <Chip color="success" label={`${audience.eligibleRecipients} email eligible`} />
                <Chip label={`${audience.exclusions.unregistered} unregistered`} />
                <Chip label={`${audience.exclusions.opted_out} opted out`} />
                <Chip label={`${audience.exclusions.invalid_email} invalid email`} />
                <Chip label={`${audience.exclusions.duplicate_email} duplicate email`} />
              </Box>
            </Paper>
            <TableContainer component={Paper} sx={{ mb: 3, maxHeight: 520 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>User</TableCell>
                    <TableCell>Language</TableCell>
                    <TableCell>Memberships</TableCell>
                    <TableCell>Matching businesses</TableCell>
                    <TableCell>Services / Providers / Appts</TableCell>
                    <TableCell>Last booking</TableCell>
                    <TableCell>Email</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {audience.rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                        No users match these filters.
                      </TableCell>
                    </TableRow>
                  ) : (
                    audience.rows.map((row) => (
                      <TableRow key={row.id}>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>
                            {row.name}
                          </Typography>
                          <Typography variant="caption" display="block">
                            {row.email}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Joined {formatDate(row.createdAt)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {row.preferredLanguage === 'es-MX'
                            ? 'Spanish (Mexico)'
                            : row.preferredLanguage === 'en-US'
                              ? 'English'
                              : row.preferredLanguage || 'Not captured'}
                        </TableCell>
                        <TableCell>
                          {row.membershipCount}
                          <Typography variant="caption" display="block" color="text.secondary">
                            {row.roles.join(', ') || 'None'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {row.matchingBusinesses.length === 0
                            ? '—'
                            : row.matchingBusinesses.map((business) => (
                                <Box key={business.id} sx={{ mb: 0.5 }}>
                                  <Typography variant="body2">{business.name}</Typography>
                                  {business.setupIssues.slice(0, 2).map((issue) => (
                                    <Chip
                                      key={issue}
                                      label={issue}
                                      size="small"
                                      color="warning"
                                      variant="outlined"
                                      sx={{ mr: 0.5, mb: 0.5 }}
                                    />
                                  ))}
                                </Box>
                              ))}
                        </TableCell>
                        <TableCell>
                          {row.matchingBusinesses
                            .map(
                              (business) =>
                                `${business.activeServiceCount} / ${business.activeProviderCount} / ${business.appointmentCount}`
                            )
                            .join(', ') || '—'}
                        </TableCell>
                        <TableCell>
                          {formatDate(row.matchingBusinesses[0]?.lastBookingAt)}
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            color={row.emailEligible ? 'success' : 'default'}
                            label={row.emailEligible ? 'Eligible' : row.exclusionReason}
                          />
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 4 }}>
              <Button
                disabled={loading || audience.page <= 1}
                onClick={() => loadPage(audience.page - 1)}
              >
                Previous
              </Button>
              <Typography>
                Page {audience.page} of {Math.max(1, audience.totalPages)}
              </Typography>
              <Button
                disabled={loading || audience.page >= audience.totalPages}
                onClick={() => loadPage(audience.page + 1)}
              >
                Next
              </Button>
            </Box>

            <Paper sx={{ p: 3 }}>
              <Typography variant="h5" sx={{ mb: 1 }}>
                Email this audience
              </Typography>
              <Typography color="text.secondary" sx={{ mb: 2 }}>
                Markdown and tokens supported: {'{{firstName}}'}, {'{{fullName}}'}, and{' '}
                {'{{businessNames}}'}.
              </Typography>
              <TextField
                fullWidth
                label="Subject"
                value={subject}
                onChange={(event) => setSubject(event.target.value)}
                inputProps={{ maxLength: 200 }}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                multiline
                minRows={9}
                label="Markdown body"
                value={markdownBody}
                onChange={(event) => setMarkdownBody(event.target.value)}
                inputProps={{ maxLength: 20_000 }}
                sx={{ mb: 2 }}
              />
              <Button
                variant="contained"
                onClick={openReview}
                disabled={
                  loading ||
                  filtersDirty ||
                  audience.eligibleRecipients === 0 ||
                  !subject.trim() ||
                  !markdownBody.trim()
                }
              >
                Review email
              </Button>
              {campaign && (
                <Alert
                  severity={
                    campaign.status === 'failed'
                      ? 'error'
                      : campaign.status === 'completed'
                        ? 'success'
                        : 'info'
                  }
                  sx={{ mt: 2 }}
                >
                  Campaign {campaign.status}: {campaign.sent} sent, {campaign.failed} failed,{' '}
                  {campaign.excluded} excluded of {campaign.total}.
                </Alert>
              )}
            </Paper>
          </>
        )}
      </Box>

      <Dialog open={reviewOpen} onClose={() => setReviewOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Review email and recipients</DialogTitle>
        <DialogContent>
          {review && audience && (
            <>
              <Alert severity="warning" sx={{ mb: 2 }}>
                This will send one email to {audience.eligibleRecipients} eligible users.
              </Alert>
              <Typography variant="subtitle2">Rendered subject</Typography>
              <Typography sx={{ mb: 2 }}>{review.renderedSubject}</Typography>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Sample email
              </Typography>
              <Paper
                variant="outlined"
                sx={{ p: 2, mb: 2 }}
                dangerouslySetInnerHTML={{ __html: review.renderedHtml }}
              />
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
                <Button
                  variant="outlined"
                  onClick={sendTest}
                  disabled={testSending || !user?.email}
                >
                  {testSending ? 'Sending…' : `Send test to ${user?.email ?? 'admin'}`}
                </Button>
                {testSent && <Chip color="success" label="Test sent" />}
              </Box>
              <TextField
                fullWidth
                label={`Type ${audience.eligibleRecipients} to confirm`}
                value={confirmCount}
                onChange={(event) => setConfirmCount(event.target.value)}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReviewOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={startCampaign}
            disabled={!audience || Number(confirmCount) !== audience.eligibleRecipients || loading}
          >
            Send campaign
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
